import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageCircle, QrCode, Unplug, CheckCircle2, Bot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Field } from "@/components/common";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const BADGE = {
  connected: ["Conectado", "bg-emerald-500/15 text-emerald-500"],
  connecting: ["Aguardando leitura do QR Code", "bg-amber-500/15 text-amber-500"],
  disconnected: ["Desconectado", "bg-red-500/15 text-red-400"],
};

function StatusBadge({ status }) {
  const [label, cls] = BADGE[status] || BADGE.disconnected;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${cls}`} data-testid="wa-status"><span className="h-2 w-2 rounded-full bg-current" />{label}</span>;
}

function QrBox({ qr, expired, onRetry }) {
  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border bg-white p-6 text-slate-800 sm:flex-row sm:items-start" data-testid="wa-qr-box">
      <div className="grid h-64 w-64 shrink-0 place-items-center rounded-xl border">
        {expired ? <Button onClick={onRetry} data-testid="wa-qr-retry-btn">Gerar novo QR Code</Button>
          : qr.qr ? <img src={qr.qr} alt="QR Code do WhatsApp" className="h-60 w-60" data-testid="wa-qr-image" /> : <Loader2 className="h-8 w-8 animate-spin text-slate-400" />}
      </div>
      <div className="space-y-3 text-sm">
        <p className="font-semibold">Escaneie com o celular da loja</p>
        <ol className="list-decimal space-y-1 pl-5 text-slate-600">
          <li>Abra o WhatsApp no celular</li>
          <li>Toque em Mais opções (⋮) ou Configurações e depois em <b>Aparelhos conectados</b></li>
          <li>Toque em <b>Conectar um aparelho</b> e aponte a câmera para o QR Code</li>
        </ol>
        {qr.pairing_code && <p className="text-xs text-slate-500">Ou conecte com o código: <b className="font-mono tracking-widest" data-testid="wa-pairing-code">{qr.pairing_code}</b></p>}
        <p className="text-xs text-slate-500">O QR Code se renova sozinho. A tela confere a conexão a cada 5 segundos.</p>
      </div>
    </div>
  );
}

const STATUS_LABEL = { confirmado: "Aceito", preparo: "Em preparação", pronto: "Pronto (só retirada)", entrega: "Saiu para entrega", entregue: "Entregue", cancelado: "Cancelado" };

function BotPreview({ text, statuses }) {
  return (
    <div className="card-surface space-y-3 p-5" data-testid="wa-bot-preview">
      <p className="flex items-center gap-2 font-semibold"><Bot className="h-4 w-4 text-primary" />O que o bot responde</p>
      <pre className="whitespace-pre-wrap rounded-xl bg-emerald-500/10 p-4 font-sans text-sm" data-testid="wa-welcome-preview">{text}</pre>
      <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
        <li>As boas-vindas vão na primeira mensagem do cliente e de novo depois de 1 hora sem conversa.</li>
        <li>No meio da conversa, a IA responde com os dados da loja: endereço, horário, cardápio, formas de pagamento e o status dos pedidos daquele número.</li>
        <li>Quando um atendente responde o cliente pelo WhatsApp, o bot fica 30 minutos em silêncio com aquele cliente (contados da primeira resposta do atendente).</li>
        <li>Durante a pausa, se o cliente escrever e ninguém responder em 1 minuto, toca um alerta no sistema e chega um aviso no número de alertas.</li>
        <li>Não responde grupos nem mensagens enviadas pela própria loja.</li>
      </ul>
      <p className="pt-2 text-sm font-semibold">Avisos automáticos de status (delivery e retirada)</p>
      <ul className="space-y-1 text-sm" data-testid="wa-status-messages">
        {Object.entries(statuses || {}).map(([k, v]) => <li key={k}><b>{STATUS_LABEL[k] || k}:</b> <span className="text-muted-foreground">{v}</span></li>)}
      </ul>
    </div>
  );
}

function AlertNumber({ wa, reload }) {
  const [v, setV] = useState(wa.alert_number || "");
  const [busy, setBusy] = useState(false);
  const digits = v.replace(/\D/g, "");
  const save = async () => {
    setBusy(true);
    try { await api.put("/whatsapp/settings", { alert_number: digits }); toast.success(digits ? "Número de alertas salvo" : "Número de alertas removido"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_auto]">
      <Field label="Número para alertas (opcional)" hint="Ex.: celular do gerente. Recebe um aviso quando um cliente fica 1 minuto sem resposta durante a pausa do bot">
        <Input value={v} onChange={(e) => setV(e.target.value)} inputMode="numeric" placeholder="5547988880000" className="font-mono" data-testid="wa-alert-number-input" />
      </Field>
      <Button variant="outline" className="sm:mb-5" onClick={save} disabled={busy || (digits.length > 0 && digits.length < 12)} data-testid="wa-alert-number-save">Salvar</Button>
    </div>
  );
}

function useQrPolling(qr, setQr, connected, onConnected) {
  const [tries, setTries] = useState(0);
  const waiting = !!qr && !connected;
  useEffect(() => {
    if (!waiting) return undefined;
    const id = setInterval(() => api.get("/whatsapp/status").then((r) => r.data.status === "connected" && onConnected(r.data)).catch(() => {}), 5000);
    return () => clearInterval(id);
  }, [waiting, onConnected]);
  useEffect(() => {
    if (!waiting || tries >= 6) return undefined;
    const id = setTimeout(() => { api.get("/whatsapp/qr").then((r) => setQr(r.data)).catch(() => {}); setTries((n) => n + 1); }, 30000);
    return () => clearTimeout(id);
  }, [waiting, tries, setQr]);
  return { expired: tries >= 6, reset: () => setTries(0) };
}

export function WhatsAppTab() {
  const [wa, setWa] = useState(null);
  const [number, setNumber] = useState("");
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const load = useCallback(() => api.get("/whatsapp", { params: { origin: window.location.origin } }).then((r) => { setWa(r.data); setNumber((n) => n || r.data.number); }).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const onConnected = useCallback(() => { setQr(null); toast.success("WhatsApp conectado!"); load(); }, [load]);
  const connected = wa?.status === "connected";
  const poll = useQrPolling(qr, setQr, connected, onConnected);
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/connect", { number: number.replace(/\D/g, ""), origin: window.location.origin });
      poll.reset();
      if (data.status === "connected") onConnected(); else { setQr(data); setWa((w) => ({ ...w, status: "connecting" })); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/whatsapp/disconnect"); setQr(null); toast.success("WhatsApp desconectado"); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); setConfirm(false); }
  };
  if (!wa) return <div className="grid place-items-center p-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const digits = number.replace(/\D/g, "");
  return (
    <div className="space-y-4" data-testid="whatsapp-tab">
      <div className="card-surface space-y-5 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className="flex items-center gap-2 font-display text-lg font-semibold"><MessageCircle className="h-5 w-5 text-emerald-500" />WhatsApp Bot</p>
            <p className="text-xs text-muted-foreground">Instância: <span className="font-mono">{wa.instance}</span></p></div>
          <StatusBadge status={wa.status} />
        </div>
        {!wa.configured && <p className="rounded-lg bg-amber-500/15 p-3 text-sm text-amber-500" data-testid="wa-not-configured">A chave da Evolution API (EVOLUTION_API_KEY) ainda não foi configurada no servidor.</p>}
        {wa.warning && <p className="rounded-lg bg-amber-500/15 p-3 text-sm text-amber-500" data-testid="wa-warning">{wa.warning}</p>}
        {connected && <p className="flex items-center gap-2 rounded-lg bg-emerald-500/10 p-3 text-sm" data-testid="wa-connected-info"><CheckCircle2 className="h-4 w-4 text-emerald-500" />Conectado ao número <b>+{wa.phone || wa.number}</b>{wa.profile_name && <> ({wa.profile_name})</>}{wa.connected_at && <> desde {fmtDateTime(wa.connected_at)}</>}</p>}
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[1fr_auto]">
          <Field label="Número do WhatsApp da loja" hint="Formato internacional, só números: 55 + DDD + número. Ex.: 5547999990000">
            <Input value={number} onChange={(e) => setNumber(e.target.value)} inputMode="numeric" placeholder="5547999990000" disabled={connected} className="font-mono" data-testid="wa-number-input" />
          </Field>
          <div className="flex min-w-0 flex-wrap gap-2 sm:mb-5">
            {!connected && <Button onClick={connect} disabled={busy || !wa.configured || digits.length < 12} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-connect-btn">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <QrCode className="mr-2 h-4 w-4" />}Conectar WhatsApp</Button>}
            {(connected || wa.status === "connecting") && <Button variant="outline" onClick={() => setConfirm(true)} disabled={busy} data-testid="wa-disconnect-btn"><Unplug className="mr-2 h-4 w-4" />Desconectar</Button>}
          </div>
        </div>
        {qr && !connected && <QrBox qr={qr} expired={poll.expired} onRetry={connect} />}
        <AlertNumber key={wa.alert_number} wa={wa} reload={load} />
      </div>
      <BotPreview text={wa.welcome_preview} statuses={wa.status_messages} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="wa-disconnect-dialog">
          <AlertDialogHeader><AlertDialogTitle>Desconectar o WhatsApp?</AlertDialogTitle><AlertDialogDescription>O bot para de responder até você conectar de novo com um novo QR Code.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="wa-disconnect-cancel">Cancelar</AlertDialogCancel><AlertDialogAction onClick={disconnect} data-testid="wa-disconnect-confirm">Desconectar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
